import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Lock, ArrowRight, Search, SearchX, X } from "lucide-react";
import { fetchActivities } from "@/lib/api";
import useSeo from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { LoadingState, ErrorState } from "@/components/common/States";

const TAG_FILTERS = [
  { label: "All", match: null },
  { label: "Pack", match: "Pack" },
  { label: "Pack + Craft", match: "Pack + Craft" },
  { label: "Craft", match: "Craft" },
];

function LiveCard({ activity, onOpen }) {
  const card = activity.directoryCard || {};
  return (
    <button
      data-testid="activity-card-live"
      onClick={onOpen}
      className="group text-left rounded-3xl bg-white border border-brand-navy/10 pib-shadow overflow-hidden hover:border-brand-orange transition-all hover:-translate-y-1"
    >
      <div className="relative h-36 bg-brand-cream flex items-center justify-center overflow-hidden">
        {card.image ? (
          card.imageFit === "cover" ? (
            <>
              <img src={card.image} alt={activity.name} className="w-full h-full object-cover" style={{ objectPosition: card.imagePosition || "center" }} loading="lazy" />
              {/* Soft gradient blend into the card body */}
              <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to top, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0) 34%)" }} />
            </>
          ) : (
            <img src={card.image} alt={activity.name} className="w-full h-full object-contain p-2" loading="lazy" />
          )
        ) : (
          <span className="text-6xl" aria-hidden="true">{card.emoji}</span>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center rounded-full bg-brand-navy/5 px-2.5 py-0.5 text-xs font-semibold text-brand-navy/70">
            {card.categoryTag}
          </span>
          {activity.duration && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-navy/60">
              <Clock className="w-3.5 h-3.5" /> {activity.duration}
            </span>
          )}
        </div>
        <h3 className="display-title text-xl text-brand-navy mt-2">{activity.name}</h3>
        <p className="mt-1 text-sm text-brand-navy/70 leading-snug">{card.oneLiner || activity.shortDescription}</p>
        <span className="mt-3 inline-flex items-center gap-1.5 text-brand-orange font-semibold text-sm">
          Open guide <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </span>
      </div>
    </button>
  );
}

function ComingSoonCard({ activity }) {
  const card = activity.directoryCard || {};
  return (
    <div
      data-testid="activity-card-coming-soon"
      aria-disabled="true"
      className="rounded-3xl bg-white/60 border border-dashed border-brand-navy/15 overflow-hidden opacity-70 cursor-not-allowed"
    >
      <div className="relative h-36 bg-brand-navy/5 flex items-center justify-center overflow-hidden">
        {card.image ? (
          card.imageFit === "cover" ? (
            <img src={card.image} alt={activity.name} className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <img src={card.image} alt={activity.name} className="w-full h-full object-contain p-2" loading="lazy" />
          )
        ) : (
          <span className="text-6xl grayscale" aria-hidden="true">{card.emoji}</span>
        )}
        <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-brand-navy/70 text-white px-2.5 py-1 text-xs font-bold">
          <Lock className="w-3 h-3" /> Coming soon
        </span>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center rounded-full bg-brand-navy/5 px-2.5 py-0.5 text-xs font-semibold text-brand-navy/60">
            {card.categoryTag}
          </span>
          {activity.duration && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-navy/50">
              <Clock className="w-3.5 h-3.5" /> {activity.duration}
            </span>
          )}
        </div>
        <h3 className="display-title text-xl text-brand-navy/70 mt-2">{activity.name}</h3>
        <p className="mt-1 text-sm text-brand-navy/50 leading-snug">{card.oneLiner || activity.shortDescription}</p>
      </div>
    </div>
  );
}

export default function Directory() {
  useSeo("Choose an Activity", "Browse Project in a Box volunteering activities. Pick an activity and follow the guided setup.");
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("loading");
  const [query, setQuery] = useState("");
  const [tag, setTag] = useState("All");

  function load() {
    setStatus("loading");
    fetchActivities()
      .then((data) => {
        setItems(data);
        setStatus("ok");
      })
      .catch(() => setStatus("error"));
  }
  useEffect(load, []);

  function openActivity(slug) {
    navigate(`/activities/${slug}`);
  }

  if (status === "loading") return <LoadingState label="Loading activities…" />;
  if (status === "error") return <ErrorState onRetry={load} />;

  const live = items.filter((a) => !a.comingSoon);
  const soon = items.filter((a) => a.comingSoon);

  // Smart tag chips: only show filters that actually match at least one live
  // activity (plus "All"), so a chip can never lead to a dead empty screen.
  const presentTags = new Set(
    live.map((a) => a.directoryCard?.categoryTag).filter(Boolean)
  );
  const visibleFilters = TAG_FILTERS.filter(
    (t) => t.match === null || presentTags.has(t.match)
  );

  const q = query.trim().toLowerCase();
  const activeFilter = TAG_FILTERS.find((t) => t.label === tag) || TAG_FILTERS[0];
  const isFiltering = q !== "" || activeFilter.match !== null;

  // When searching or a tag is selected, only live activities are eligible.
  let results = live;
  if (activeFilter.match !== null) {
    results = results.filter((a) => (a.directoryCard?.categoryTag || "") === activeFilter.match);
  }
  if (q) {
    results = results.filter((a) => (a.name || "").toLowerCase().includes(q));
  }

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
      <div className="max-w-2xl">
        <h1 className="display-title text-3xl sm:text-4xl text-brand-navy">Choose an activity</h1>
        <p className="mt-2 text-brand-navy/70">
          Select an activity to view its Volunteer and Organizer guides, along with all the details of the activity.
        </p>
      </div>

      {/* Search + tag filter */}
      <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative w-full lg:max-w-sm">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-navy/40" aria-hidden="true" />
          <Input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setQuery("");
            }}
            placeholder="Search activities"
            aria-label="Search activities"
            data-testid="activity-search-input"
            className="pl-10 pr-10 rounded-full bg-white border-brand-navy/15 focus-visible:ring-brand-orange"
          />
          {query && (
            <button
              type="button"
              data-testid="clear-search"
              aria-label="Clear search"
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-6 h-6 rounded-full text-brand-navy/50 hover:bg-brand-navy/10 hover:text-brand-navy transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by tag">
          {visibleFilters.map((t) => {
            const active = t.label === tag;
            return (
              <button
                key={t.label}
                type="button"
                data-testid={`filter-chip-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                aria-pressed={active}
                onClick={() => setTag(t.label)}
                className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${
                  active
                    ? "bg-brand-orange text-white border-brand-orange"
                    : "bg-white text-brand-navy border-brand-navy/15 hover:border-brand-orange hover:text-brand-orange"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {isFiltering ? (
        results.length > 0 ? (
          <>
            <p className="mt-6 text-sm font-medium text-brand-navy/60" data-testid="results-count">
              {results.length} {results.length === 1 ? "activity" : "activities"}
            </p>
            <div className="mt-3 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {results.map((a) => (
                <LiveCard key={a.slug} activity={a} onOpen={() => openActivity(a.slug)} />
              ))}
            </div>
          </>
        ) : (
          <div
            data-testid="activities-empty-state"
            className="mt-12 flex flex-col items-center justify-center text-center rounded-3xl border border-dashed border-brand-navy/15 bg-white/60 py-14 px-6"
          >
            <SearchX className="w-8 h-8 text-brand-navy/30" aria-hidden="true" />
            <p className="mt-3 text-brand-navy/70 font-medium" data-testid="empty-state-message">
              No {activeFilter.match !== null ? `${activeFilter.label} ` : ""}activities match your search{q ? ` for "${query.trim()}"` : ""}.
            </p>
            <button
              type="button"
              data-testid="clear-filters"
              onClick={() => {
                setQuery("");
                setTag("All");
              }}
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-orange hover:underline"
            >
              Clear search and filters
            </button>
          </div>
        )
      ) : (
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {live.map((a) => (
            <LiveCard key={a.slug} activity={a} onOpen={() => openActivity(a.slug)} />
          ))}
          {soon.map((a) => (
            <ComingSoonCard key={a.slug} activity={a} />
          ))}
        </div>
      )}
    </div>
  );
}
